import { DatePipe, PercentPipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import localeGB from '@angular/common/locales/en-GB';
import {
  APP_INITIALIZER,
  ErrorHandler,
  LOCALE_ID,
  NgModule
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AmplifyAuthenticatorModule } from '@aws-amplify/ui-angular';
import { AgGridModule } from 'ag-grid-angular';
import { AngularSplitModule } from 'angular-split';
import { ToastrModule } from 'ngx-toastr';
import { AmplifyConfigService } from './amplify-config.service';
import { AppRoutingModule } from './app-routing.module';
import { AppWrapperComponent } from './app-wrapper/app-wrapper.component';
import { AppComponent } from './app.component';
import { RaygunErrorHandler } from './app.raygun.setup';
import { ExoticsComponent } from './exotics/exotics.component';
import { HttpErrorInterceptor } from './interceptors/http-error.interceptor';
import { LoginComponent } from './login/login.component';
import { MainMenuComponent } from './main-menu/main-menu.component';
import { MarketDataComponent } from './market-data/market-data.component';
import { DciTabComponent } from './modals/dci/dci-tab/dci-tab.component';
import { DciComponent } from './modals/dci/dci.component';
import { MedusaLoginComponent } from './modals/medusa-login/medusa-login.component';
import { CreditLinesComponent } from './modals/pricing-environment/credit-lines/credit-lines.component';
import { FixingsComponent } from './modals/pricing-environment/fixings/fixings.component';
import { TradeCaptureModule } from './modals/trade-capture/trade-capture.module';
import { PositionsDetailModule } from './positions-detail/positions-detail.module';
import { ProviderQuoteModule } from './provider-quote/provider-quote.module';
import { CloudwatchLogMockService, CloudwatchLogService } from './services';
import { SharedModule } from './shared/shared.module';
import { SsoComponent } from './sso/sso.component';
import { StrategiesComponent } from './strategies/strategies.component';
import { CheckboxWrapperComponent } from './ui-kit/checkbox/checkbox-wrapper.component';
import { IconCancelComponent } from './ui-kit/icons/icon-cancel/icon-cancel.component';
import { IconCheckboxTickComponent } from './ui-kit/icons/icon-checkbox-tick/icon-checkbox-tick.component';
import { IconLoaderComponent } from './ui-kit/icons/icon-loader/icon-loader.component';
import { IconTickComponent } from './ui-kit/icons/icon-tick/icon-tick.component';
import { InputComponent } from './ui-kit/input/input.component';
import { RadioWrapperComponent } from './ui-kit/radio/radio-wrapper.component';
import { SelectorUiComponent } from './ui-kit/selector/selector.component';
import { UserPreferencesComponent } from './user-preferences/user-preferences.component';
import { ActivityModalComponent } from './widgets/activity-modal/activity-modal.component';
import { DisplayDefinitionsModule } from './widgets/display-definitions/display-definitions.module';
import { WidgetsModule } from './widgets/widgets.module';
import { IRFixingsComponent } from './modals/pricing-environment/ir-fixings/ir-fixings.component';
import { PortalModule } from '@angular/cdk/portal';

registerLocaleData(localeGB, 'en-GB');

const HTTP_ERROR_INTERCEPTOR_PROVIDER = {
  provide: HTTP_INTERCEPTORS,
  useClass: HttpErrorInterceptor,
  multi: true
};

// const useServiceWorker = environment.featureFlags?.useServiceWorker ?? true;
const useServiceWorker = false;
console.log('useServiceWorker', useServiceWorker);

window.useRaygun = window.useRaygun ?? true;

export function initializeApp(configService: AmplifyConfigService) {
  return (): Promise<any> => configService.loadConfig();
}

@NgModule({
  declarations: [
    AppComponent,
    MainMenuComponent,
    StrategiesComponent,
    RadioWrapperComponent,
    CheckboxWrapperComponent,
    IconCancelComponent,
    IconTickComponent,
    IconLoaderComponent,
    IconCheckboxTickComponent,
    LoginComponent,
    ExoticsComponent,
    InputComponent,
    SelectorUiComponent,
    FixingsComponent,
    DciComponent,
    DciTabComponent,
    MedusaLoginComponent,
    SsoComponent,
    ActivityModalComponent,
    AppWrapperComponent,
    CreditLinesComponent,
    MarketDataComponent,
    IRFixingsComponent
  ],
  imports: [
    PositionsDetailModule,
    AmplifyAuthenticatorModule,
    AppRoutingModule,
    SharedModule,
    TradeCaptureModule,
    WidgetsModule,
    BrowserModule,
    BrowserAnimationsModule,
    AngularSplitModule,
    UserPreferencesComponent,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: useServiceWorker
    }),
    ProviderQuoteModule,
    DisplayDefinitionsModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-center',
      maxOpened: 3,
      autoDismiss: true,
      preventDuplicates: true,
      resetTimeoutOnDuplicate: true
    }),
    AgGridModule,
    PortalModule
  ],
  providers: [
    HTTP_ERROR_INTERCEPTOR_PROVIDER,
    {
      provide: LOCALE_ID,
      useValue: 'en-GB'
    },
    {
      provide: CloudwatchLogService,
      useClass: CloudwatchLogMockService
    },
    {
      provide: ErrorHandler,
      useClass: RaygunErrorHandler
    },
    PercentPipe,
    DatePipe,
    AmplifyConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [AmplifyConfigService],
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
